/**
 * Soft Skill Weave — scene lens + skill→icon map (Soft≠Live · 127 theatre).
 *
 * Scene boosts which skills light first; glyph silhouette follows kind∩skills.
 * Never invents Live device class / other tenants.
 */

/** Operator scenes · reshuffle icon priority Soft only. */
export const SOFT_SCENES = {
  perimeter: {
    id: "perimeter",
    label: "Perimeter",
    brief: "Fence · breach · gate",
    boost: ["patrol", "observe", "alert", "challenge"],
    ink: "#3DE1C8",
  },
  firewatch: {
    id: "firewatch",
    label: "Firewatch",
    brief: "Heat · roof · silo",
    boost: ["thermal", "overwatch", "observe", "alert"],
    ink: "#FF9B5C",
  },
  ingress: {
    id: "ingress",
    label: "Ingress",
    brief: "Human verify · walk",
    boost: ["challenge", "patrol", "observe"],
    ink: "#7FD1FF",
  },
  night: {
    id: "night",
    label: "Night",
    brief: "Thermal eyes · overwatch",
    boost: ["thermal", "overwatch", "observe", "alert"],
    ink: "#A89BFF",
  },
};

export const SOFT_SCENE_ORDER = ["perimeter", "firewatch", "ingress", "night"];

/** Skill → default glyph id (matches soft-fleet-glyphs G keys). */
const SKILL_GLYPH = {
  observe: "cam_bullet",
  thermal: "cam_ptz_thermal",
  patrol: "guard_foot",
  challenge: "dispatcher",
  alert: "siren_horn",
  overwatch: "uav_scout",
};

const KIND_GLYPH = {
  person: "guard_foot",
  drone: "uav_scout",
  robot: "ugv_patrol",
  device: "cam_bullet",
};

/**
 * Pure: pick glyph from kind + skills + active scene.
 * Primary skill = scene boost ∩ unit skills · else first skill · else kind.
 */
export function glyphForSoftUnit(props) {
  if (!props) return "cam_bullet";
  if (props.glyph && typeof props.glyph === "string") return props.glyph;
  const skillsRaw = props.skills;
  const skills = Array.isArray(skillsRaw)
    ? skillsRaw
    : String(skillsRaw || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
  const sceneId = String(props.scene || "perimeter");
  const scene = SOFT_SCENES[sceneId] || SOFT_SCENES.perimeter;
  const boost = scene.boost || [];
  let primary = null;
  for (const s of boost) {
    if (skills.includes(s)) {
      primary = s;
      break;
    }
  }
  if (!primary && skills.length) primary = skills[0];

  if (props.kind === "drone" && (skills.includes("thermal") || primary === "thermal")) {
    return "uav_thermal";
  }
  if (props.kind === "robot" && skills.includes("alert")) return "ugv_signal";
  if (props.kind === "person" && skills.includes("challenge") && boost[0] === "challenge") {
    return "dispatcher";
  }
  if (props.kind === "person" && skills.includes("challenge") && primary === "challenge") {
    return "guard_lead";
  }
  if (props.kind === "device" && skills.includes("thermal")) return "cam_ptz_thermal";
  if (props.kind === "device" && skills.includes("alert")) return "siren_horn";
  if (props.kind === "device" && String(props.id || "").toUpperCase().includes("GW")) {
    return "mesh_gateway";
  }

  if (primary && SKILL_GLYPH[primary]) return SKILL_GLYPH[primary];
  return KIND_GLYPH[props.kind] || "cam_bullet";
}

/** Pure: scene list for UI chips. */
export function softSceneList() {
  return SOFT_SCENE_ORDER.map((id) => SOFT_SCENES[id]);
}

/**
 * Pure: Live register row → Soft place tip (survey honesty).
 * map_lat / map_lng or ll — mirror only, never Live write.
 */
export function surveyedLlOf(device) {
  if (!device || typeof device !== "object") return null;
  if (Array.isArray(device.ll) && device.ll.length >= 2) {
    const lng = Number(device.ll[0]);
    const lat = Number(device.ll[1]);
    if (Number.isFinite(lng) && Number.isFinite(lat)) return [lng, lat];
  }
  const lat = Number(device.map_lat ?? device.lat ?? device.latitude);
  const lng = Number(device.map_lng ?? device.lng ?? device.longitude);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return [lng, lat];
  return null;
}

/**
 * Pure: best free Soft unit for scene (skill hit count). Soft≠Live.
 */
export function suggestSoftUnitId(state) {
  if (!state || !Array.isArray(state.assets)) return null;
  const scene = SOFT_SCENES[state.scene] || SOFT_SCENES.perimeter;
  const boost = scene.boost || [];
  let best = null;
  let bestScore = -1;
  for (const a of state.assets) {
    if (!a || !a.free) continue;
    const skills = a.skills || [];
    let score = 0;
    for (const s of boost) if (skills.includes(s)) score += 2;
    score += skills.length * 0.1;
    if (a.kind === "person") score += 0.05;
    if (score > bestScore) {
      bestScore = score;
      best = a.id;
    }
  }
  return best;
}
